import { useAuth } from '@eg/compartido'
import { useQuery } from '@tanstack/react-query'
import { DEMO, TIPO_CAMBIO_DEMO } from '../../demo/modo'
import { useMiEmpresa } from '../empresa/datos'

/** `usd` solo viene cuando el precio se fijó en dólares: `importe` ya es la conversión a pesos al tipo de cambio del catálogo. */
export type PrecioFila = { importe: number; moneda: string; lista_precio_id: string; usd?: number }
export type Presentacion = {
  id: string
  sku: string
  contenido: string
  activa: boolean
  pedido_minimo: number
  pedido_multiplo: number
  precios: PrecioFila[]
}
export type ProductoCatalogo = {
  id: string
  slug: string
  nombre_comercial: string
  marca: string | null
  descripcion: string | null
  imagen_ruta: string | null
  iva_porcentaje: number
  ingredientes: string | null
  modo_de_uso: string | null
  categoria: { nombre: string; slug: string } | null
  presentaciones: Presentacion[]
}

export type InfoLista = { es_publica: boolean; iva_incluido: boolean }

/** Tipo de cambio con el que se convierten los precios en dólares (en la demostración es de referencia). */
export type TipoCambio = { usdMxn: number; fecha: string }

const redondear = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** Pasa a pesos los precios fijados en dólares y descarta los que no se pueden convertir (nunca se muestra un importe sin moneda clara). */
export function convertirPrecios(productos: ProductoCatalogo[], tc: TipoCambio | null): ProductoCatalogo[] {
  return productos.map((p) => ({
    ...p,
    presentaciones: p.presentaciones.map((pr) => ({
      ...pr,
      precios: pr.precios.flatMap((x): PrecioFila[] => {
        if (x.moneda !== 'USD') return [x]
        if (!tc) return []
        return [{ ...x, moneda: 'MXN', importe: redondear(x.importe * tc.usdMxn), usd: x.importe }]
      }),
    })),
  }))
}

export type Catalogo = {
  /** Tipo de cambio de los precios en dólares; null si todos los precios están en pesos. */
  tipoCambio: TipoCambio | null
  productos: ProductoCatalogo[]
  /** Id de la lista de precio público (null si aún no existe). */
  listaPublicaId: string | null
  /** Listas visibles para quien consulta: la pública y, si es socio verificado, la suya. */
  listas: Record<string, InfoLista>
}

const COLUMNAS =
  'id, slug, nombre_comercial, marca, descripcion, imagen_ruta, iva_porcentaje, ingredientes, modo_de_uso, ' +
  'categoria:categorias(nombre, slug), ' +
  'presentaciones(id, sku, contenido, activa, pedido_minimo, pedido_multiplo, precios(importe, moneda, lista_precio_id))'

/**
 * Productos publicados con sus presentaciones y precios. La base decide qué precios llegan: sin sesión solo
 * el público; un socio verificado recibe además los de su lista. El navegador solo elige cuál mostrar.
 */
export function useCatalogo() {
  const { cliente, sesion } = useAuth()
  return useQuery({
    queryKey: ['catalogo', sesion?.user.id ?? 'anon'],
    enabled: cliente !== null,
    queryFn: async (): Promise<Catalogo> => {
      const [prod, listas] = await Promise.all([
        cliente!.from('productos').select(COLUMNAS).eq('publicado', true).order('nombre_comercial'),
        cliente!.from('listas_precio').select('id, es_publica, iva_incluido'),
      ])
      if (prod.error) throw prod.error
      if (listas.error) throw listas.error
      const listasMapa: Record<string, InfoLista> = {}
      let publica: string | null = null
      for (const l of listas.data) {
        listasMapa[l.id] = { es_publica: l.es_publica, iva_incluido: l.iva_incluido }
        if (l.es_publica) publica = l.id
      }
      return { tipoCambio: DEMO ? TIPO_CAMBIO_DEMO : null, productos: convertirPrecios(prod.data as unknown as ProductoCatalogo[], DEMO ? TIPO_CAMBIO_DEMO : null), listaPublicaId: publica, listas: listasMapa }
    },
  })
}

/** Lista de precios del socio verificado (null si la persona no es socia o aún no está verificada). */
export function useListaSocio(): string | null {
  const { data } = useMiEmpresa()
  const e = data?.empresa
  return e && e.estado === 'verificada' ? e.lista_precio_id : null
}

export type PreciosVista = { lista: number | null; socio: number | null }

/** Menor precio vigente de una presentación en la lista pública y en la del socio. */
export function preciosDe(p: Presentacion, listaPublicaId: string | null, listaSocioId: string | null): PreciosVista {
  const de = (id: string | null) => {
    if (!id) return null
    const v = p.precios.filter((x) => x.lista_precio_id === id).map((x) => x.importe)
    return v.length ? Math.min(...v) : null
  }
  return { lista: de(listaPublicaId), socio: de(listaSocioId) }
}

/** Precio más bajo del producto: el del socio si existe, y si no el de lista. */
export function precioDesde(prod: ProductoCatalogo, listaPublicaId: string | null, listaSocioId: string | null): { importe: number; esSocio: boolean; usd?: number } | null {
  let mejor: { importe: number; esSocio: boolean; usd?: number } | null = null
  for (const pres of prod.presentaciones.filter((x) => x.activa)) {
    const v = preciosDe(pres, listaPublicaId, listaSocioId)
    const usdDe = (importe: number, id: string | null) => pres.precios.find((x) => x.lista_precio_id === id && x.importe === importe)?.usd
    const candidato =
      v.socio !== null
        ? { importe: v.socio, esSocio: true, usd: usdDe(v.socio, listaSocioId) }
        : v.lista !== null
          ? { importe: v.lista, esSocio: false, usd: usdDe(v.lista, listaPublicaId) }
          : null
    if (candidato && (!mejor || candidato.importe < mejor.importe)) mejor = candidato
  }
  return mejor
}

export type PrecioElegido = { importe: number; incluyeIva: boolean; esSocio: boolean; usd?: number }

/** Precio que aplica a una presentación: el de la lista del socio si lo hay; si no, el público. Igual que el servidor. */
export function precioVigenteDe(p: Presentacion, publica: string | null, socio: string | null, listas: Record<string, InfoLista>): PrecioElegido | null {
  const de = (id: string | null) => {
    if (!id) return null
    const v = p.precios.filter((x) => x.lista_precio_id === id).sort((a, b) => a.importe - b.importe)[0]
    return v ?? null
  }
  const delSocio = de(socio)
  if (socio && delSocio) return { importe: delSocio.importe, usd: delSocio.usd, incluyeIva: listas[socio]?.iva_incluido ?? false, esSocio: true }
  const delPublico = de(publica)
  if (publica && delPublico) return { importe: delPublico.importe, usd: delPublico.usd, incluyeIva: listas[publica]?.iva_incluido ?? true, esSocio: false }
  return null
}
